import {
  isModelVirtualWeatherStation,
  isVirtualWeatherStation
} from '@weenat/client/dist/core/devices'
import {
  MetricSummaryValue as MetricSummaryValueInterface,
  useMetricSummary
} from '@weenat/client/dist/core/metrics'
import { isDevice, isPlot, isStation } from '@weenat/client/dist/core/utils/resourcesTypeCheckers'
import { Device, DeviceAvailableMeasure } from '@weenat/client/dist/resources/devices'
import { ForecastResults } from '@weenat/client/dist/resources/forecasts'
import { SummaryData } from '@weenat/client/dist/resources/measurements.type'
import { PlotAvailableMeasure } from '@weenat/client/dist/resources/plots'
import { Station } from '@weenat/client/dist/resources/stations'
import { useIsAppAccessRestricted } from 'app/src/hooks/useIsAppAccessRestricted'
import { useToggleFeature } from 'app/state'
import { useCallback, useMemo } from 'react'
import { useTheme } from 'styled-components'
import MetricSummaryValue from './MetricSummaryValue'

interface DumbMetricSummaryCondensedProps {
  /** Timestamp at which the latest data was received */
  metric: DeviceAvailableMeasure | PlotAvailableMeasure
  forecastsResults: ForecastResults | undefined
  resource: Device | Station
  summary: SummaryData
  isExpertOrg: boolean
}

const DumbMetricSummaryCondensed: FC<DumbMetricSummaryCondensedProps> = ({
  metric,
  forecastsResults,
  resource,
  summary,
  isExpertOrg
}) => {
  const { colors, typography } = useTheme()
  const { reason } = useIsAppAccessRestricted({
    isVWS: isPlot(resource)
      ? undefined
      : (isDevice(resource) && isVirtualWeatherStation(resource)) ||
        (isStation(resource) && isModelVirtualWeatherStation(resource.model))
  })
  const [{ smv }] = useToggleFeature()

  const { getFullSummaryValue } = useMetricSummary({
    metric,
    forecastsResults,
    resource,
    summary,
    isExpertOrg,
    smv
  })

  const getMetricSummaries = useCallback(
    (aMetric: DeviceAvailableMeasure | PlotAvailableMeasure): MetricSummaryValueInterface[] => {
      const metricSummaries = []
      switch (aMetric) {
        case 'U':
        case 'T_SOIL':
        case 'T_CAL':
        case 'T_WET':
        case 'T_DRY':
        case 'T':
        case 'FF':
        case 'SSI':
        case 'T_DEW':
        case 'PPFD':
        case 'LW_V':
          metricSummaries.push(getFullSummaryValue('latest'))
          break
        case 'RR':
          metricSummaries.push(getFullSummaryValue('today'))
          metricSummaries.push(getFullSummaryValue('yesterday'))
          metricSummaries.push(getFullSummaryValue('week'))
          break
        default:
          break
      }
      return metricSummaries
    },
    [getFullSummaryValue]
  )

  const metricSummaries = useMemo(() => getMetricSummaries(metric), [getMetricSummaries, metric])

  const labelStyle = {
    color: colors.grayscale.black,
    fontSize: `${typography.sizes.xs}px`,
    fontWeight: typography.weights.light
  }

  return (
    <Flex $flex={1} $gap='md' $p='md' $alignItems='center' $justifyContent='space-around'>
      {metricSummaries.map((mSummary) => (
        <MetricSummaryValue
          metric={metric}
          key={`${mSummary.aggregationId}_${mSummary.label}`}
          showLabel={metricSummaries.length > 1}
          isShrunk
          summaryValue={mSummary}
          reason={reason}
          labelStyle={labelStyle}
        />
      ))}
    </Flex>
  )
}

export default DumbMetricSummaryCondensed
